import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService } from '@/services/k8sResourceService'
import SearchableInput from '@/components/ui/SearchableInput'
import SearchableSelector from '@/components/ui/SearchableSelector'

interface AnnotationItem {
  id: string
  key: string
  value: string
}

interface IngressConfigEditorProps {
  flow: any
  canEdit: boolean
  onSave: (updatedManifest: any, configName: string) => void
}

export default function IngressConfigEditor({ flow, canEdit, onSave }: IngressConfigEditorProps) {
  const { t } = useTranslation(['pipeline-studio', 'common'])
  const [isEditing, setIsEditing] = useState(false)

  // Edit state
  const [editedEnabled, setEditedEnabled] = useState(false)
  const [editedHost, setEditedHost] = useState('')
  const [editedClassName, setEditedClassName] = useState('')
  const [editedTLSEnabled, setEditedTLSEnabled] = useState(false)
  const [editedTLSSecret, setEditedTLSSecret] = useState('')
  const [annotationItems, setAnnotationItems] = useState<AnnotationItem[]>([])

  // TLS Secrets state
  const [tlsSecrets, setTlsSecrets] = useState<string[]>([])
  const [secretsLoading, setSecretsLoading] = useState(false)

  // Ingress Classes state
  const [ingressClasses, setIngressClasses] = useState<string[]>([])
  const [ingressClassesLoading, setIngressClassesLoading] = useState(false)

  // Fetch TLS secrets when editing
  useEffect(() => {
    const fetchTLSSecrets = async () => {
      if (!isEditing || !flow.metadata?.namespace) return

      setSecretsLoading(true)
      try {
        const secrets = await k8sResourceService.getSecrets(flow.metadata.namespace)
        const tlsSecretNames = k8sResourceService.extractTLSSecretNames(secrets)
        setTlsSecrets(tlsSecretNames)
      } catch (error) {
        console.error('Failed to fetch TLS secrets:', error)
        setTlsSecrets([])
      } finally {
        setSecretsLoading(false)
      }
    }

    fetchTLSSecrets()
  }, [isEditing, flow.metadata?.namespace])

  // Fetch Ingress Classes when editing
  useEffect(() => {
    const fetchIngressClasses = async () => {
      if (!isEditing) return

      setIngressClassesLoading(true)
      try {
        const classes = await k8sResourceService.getIngressClasses()
        const classNames = k8sResourceService.extractIngressClassNames(classes)
        setIngressClasses(classNames)
      } catch (error) {
        console.error('Failed to fetch ingress classes:', error)
        setIngressClasses(['nginx', 'traefik', 'haproxy']) // Fallback
      } finally {
        setIngressClassesLoading(false)
      }
    }

    fetchIngressClasses()
  }, [isEditing])

  // Annotation helper functions
  const convertToAnnotationItems = (annotations: Record<string, string> = {}): AnnotationItem[] => {
    return Object.entries(annotations).map(([key, value], index) => ({
      id: `annotation-${Date.now()}-${index}`,
      key,
      value
    }))
  }

  const convertToAnnotationsRecord = (items: AnnotationItem[]): Record<string, string> => {
    const record: Record<string, string> = {}
    items.forEach(item => {
      if (item.key.trim()) {
        record[item.key] = item.value
      }
    })
    return record
  }

  const addAnnotation = () => {
    const newItem: AnnotationItem = {
      id: `annotation-${Date.now()}-${Math.random()}`,
      key: '',
      value: ''
    }
    setAnnotationItems([...annotationItems, newItem])
  }

  const removeAnnotation = (id: string) => {
    setAnnotationItems(annotationItems.filter(item => item.id !== id))
  }

  const updateAnnotationKey = (id: string, newKey: string) => {
    setAnnotationItems(annotationItems.map(item =>
      item.id === id ? { ...item, key: newKey } : item
    ))
  }

  const updateAnnotationValue = (id: string, newValue: string) => {
    setAnnotationItems(annotationItems.map(item =>
      item.id === id ? { ...item, value: newValue } : item
    ))
  }

  const handleEdit = () => {
    setEditedEnabled(flow.spec?.ingress?.enabled || false)
    setEditedHost(flow.spec?.ingress?.host || '')
    setEditedClassName(flow.spec?.ingress?.className || 'nginx')
    setEditedTLSEnabled(flow.spec?.ingress?.tls?.enabled || false)
    setEditedTLSSecret(flow.spec?.ingress?.tls?.secretName || '')
    setAnnotationItems(convertToAnnotationItems(flow.spec?.ingress?.annotations || {}))
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSave = () => {
    const annotations = convertToAnnotationsRecord(annotationItems)

    const updatedFlow = {
      ...flow,
      spec: {
        ...flow.spec,
        ingress: editedEnabled ? {
          enabled: true,
          host: editedHost.trim(),
          className: editedClassName.trim() || 'nginx',
          ...(Object.keys(annotations).length > 0 && { annotations }),
          ...(editedTLSEnabled && {
            tls: {
              enabled: true,
              secretName: editedTLSSecret.trim() || undefined
            }
          })
        } : {
          enabled: false
        }
      }
    }

    if (updatedFlow.spec.ingress.tls && !updatedFlow.spec.ingress.tls.secretName) {
      delete updatedFlow.spec.ingress.tls.secretName
    }

    onSave(updatedFlow, t('detail.ingressConfig'))
    setIsEditing(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-text-dark">{t('detail.ingressConfig', 'Ingress Configuration')}</h2>
        {canEdit && !isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-primary hover:bg-gray-50 rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('detail.edit', 'Edit')}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {/* Enabled */}
          <div className="flex items-center">
            <input
              id="ingressEnabled"
              type="checkbox"
              checked={editedEnabled}
              onChange={(e) => setEditedEnabled(e.target.checked)}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            />
            <label htmlFor="ingressEnabled" className="ml-2 block text-sm text-gray-900">
              {t('detail.ingressEnabled', 'Enabled')}
            </label>
          </div>

          {editedEnabled && (
            <>
              {/* Host */}
              <div>
                <label htmlFor="host" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('detail.ingressHost', 'Host')} <span className="text-red-500">*</span>
                </label>
                <input
                  id="host"
                  type="text"
                  value={editedHost}
                  onChange={(e) => setEditedHost(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  placeholder="example.com"
                  required
                />
              </div>

              {/* Ingress Class */}
              <div>
                <SearchableSelector
                  title={t('detail.ingressClassName', 'Ingress Class')}
                  value={editedClassName}
                  onChange={(val) => setEditedClassName(val)}
                  options={ingressClasses}
                  placeholder="Select ingress class..."
                  loading={ingressClassesLoading}
                  disabled={false}
                  emptyMessage="No ingress classes available"
                  allowClear={false}
                  showCount={false}
                />
              </div>

              {/* TLS Enabled */}
              <div className="flex items-center">
                <input
                  id="tlsEnabled"
                  type="checkbox"
                  checked={editedTLSEnabled}
                  onChange={(e) => setEditedTLSEnabled(e.target.checked)}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="tlsEnabled" className="ml-2 block text-sm text-gray-900">
                  {t('detail.ingressTLSEnabled', 'TLS Enabled')}
                </label>
              </div>

              {/* TLS Secret */}
              {editedTLSEnabled && (
                <div>
                  <SearchableInput
                    title={t('detail.ingressTLSSecret', 'TLS Secret')}
                    value={editedTLSSecret}
                    onChange={(val) => setEditedTLSSecret(val)}
                    options={tlsSecrets}
                    placeholder="Type or select TLS secret..."
                    loading={secretsLoading}
                    disabled={false}
                    emptyMessage={`No TLS secrets in ${flow.metadata?.namespace || 'namespace'}`}
                    allowCustom={true}
                    showCount={false}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    You can select existing TLS secrets or type a custom name (e.g., for cert-manager auto-generation)
                  </p>
                </div>
              )}

              {/* Annotations */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <label className="text-sm font-medium text-gray-900">
                      {t('detail.ingressAnnotations', 'Annotations')}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Add custom annotations for ingress configuration
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addAnnotation}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Annotation
                  </button>
                </div>

                {annotationItems.length > 0 ? (
                  <div className="space-y-2">
                    {annotationItems.map((item) => (
                      <div key={item.id} className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex gap-3 items-center">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={item.key}
                              onChange={(e) => updateAnnotationKey(item.id, e.target.value)}
                              placeholder="nginx.ingress.kubernetes.io/ssl-redirect"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>
                          <div className="flex-1">
                            <input
                              type="text"
                              value={item.value}
                              onChange={(e) => updateAnnotationValue(item.id, e.target.value)}
                              placeholder="true"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAnnotation(item.id)}
                            className="flex items-center justify-center w-9 h-9 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                            title="Remove annotation"
                          >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <div className="w-8 h-8 text-gray-400 mb-2">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                      </svg>
                    </div>
                    <p className="text-xs text-gray-500 text-center font-medium">No annotations added</p>
                    <p className="text-xs text-gray-400 text-center mt-1">Click "Add Annotation" to get started</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={editedEnabled && !editedHost.trim()}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('detail.save', 'Save')}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('detail.ingressEnabled', 'Enabled')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{flow.spec?.ingress?.enabled ? 'Yes' : 'No'}</dd>
          </div>
          {flow.spec?.ingress?.enabled && (
            <>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.ingressHost', 'Host')}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{flow.spec.ingress.host || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.ingressClassName', 'Class Name')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.ingress.className || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.ingressTLSEnabled', 'TLS Enabled')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{flow.spec.ingress.tls?.enabled ? 'Yes' : 'No'}</dd>
              </div>
              {flow.spec.ingress.tls?.enabled && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('detail.ingressTLSSecret', 'TLS Secret')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{flow.spec.ingress.tls.secretName || '-'}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('detail.ingressAnnotations', 'Annotations')}</dt>
                <dd className="mt-1">
                  {flow.spec.ingress.annotations && Object.keys(flow.spec.ingress.annotations).length > 0 ? (
                    <div className="space-y-1">
                      {Object.entries(flow.spec.ingress.annotations).map(([key, value]) => (
                        <div key={key} className="flex items-start gap-2 text-sm">
                          <span className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{key}</span>
                          <span className="text-gray-500">:</span>
                          <span className="font-mono text-gray-900">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">-</span>
                  )}
                </dd>
              </div>
            </>
          )}
        </dl>
      )}
    </div>
  )
}
